import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Trophy, Award, Star, Zap } from "lucide-react";
import { motion } from "framer-motion";

interface Achievement {
  id: string;
  name: string;
  description: string;
  icon: string;
  badge_color: string;
  xp_reward: number;
  category: string;
  requirement_type: string;
  requirement_value: number;
  unlocked_at?: string;
}

interface AchievementBadgeProps {
  achievement: Achievement;
  isUnlocked?: boolean;
}

const AchievementBadge = ({ achievement, isUnlocked = false }: AchievementBadgeProps) => {
  const getIconComponent = (iconName: string) => {
    switch (iconName.toLowerCase()) {
      case 'trophy':
        return Trophy;
      case 'award':
        return Award;
      case 'star':
        return Star;
      case 'zap':
        return Zap;
      default:
        return Trophy;
    }
  };

  const IconComponent = getIconComponent(achievement.icon);

  const getBadgeColorClass = (color: string) => {
    switch (color.toLowerCase()) {
      case 'gold':
        return 'from-yellow-400 to-yellow-600';
      case 'silver':
        return 'from-gray-300 to-gray-500';
      case 'bronze':
        return 'from-amber-600 to-amber-800';
      case 'blue':
        return 'from-blue-400 to-blue-600';
      case 'purple':
        return 'from-purple-400 to-purple-600';
      case 'green':
        return 'from-primary to-primary';
      default:
        return 'from-gray-400 to-gray-600';
    }
  };

  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Card 
        className={`overflow-hidden transition-all hover:shadow-lg ${
          isUnlocked ? '' : 'opacity-50 grayscale'
        }`}
      >
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div 
              className={`
                w-12 h-12 rounded-full flex items-center justify-center
                bg-gradient-to-br ${getBadgeColorClass(achievement.badge_color)}
                ${isUnlocked ? 'shadow-lg' : ''}
              `}
            >
              <IconComponent className="h-6 w-6 text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-sm leading-tight">
                  {achievement.name}
                </h4>
                {isUnlocked && (
                  <Badge variant="secondary" className="text-xs">
                    +{achievement.xp_reward} XP
                  </Badge>
                )}
              </div>
              
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {achievement.description}
              </p>
              
              <div className="flex items-center gap-2 mt-2">
                <Badge variant="outline" className="text-xs">
                  {achievement.category}
                </Badge>
                {isUnlocked && achievement.unlocked_at && (
                  <span className="text-xs text-muted-foreground">
                    Unlocked {new Date(achievement.unlocked_at).toLocaleDateString()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default AchievementBadge;
